import { app } from "@/main.js"

const defaultState = () => {
	return {
		accessToken: null,
		organisation_name: null,
		organisation: null,
		organisations: null,
		org_id: null,
		organisation_id: null, // Added to ensure complete state schema definition
		isOrgAdmin: false,
		hasOrgSettingsAccess: false,
		hasXSurflogAccess: false,
		temp_orgs: null,
		organisation_uuid: null,
		isAuthenticated: false,
		tokenExpiry: null,
		jwtPayload: null,
		isSuperAdmin: false,
		hasVertexRole: false,
		roles: [],
		vertex_roles: [],
		token: null,
		loginLoop: 0,
		isTokenExpired: false,
		isVertexAnalyst: false,
		isForbidden: false,
		fromSwitchBool: false,
		fromAuthSwitchProduct: false,
		fromAuthSwitchProductId: 0,
		logo: ''
	}
}

const state = () => ({
	accessToken: null,
	organisation_name: null,
	organisation: null,
	organisations: null,
	redirectLink: null,
	org_id: null,
	organisation_id: null, // Added to ensure complete state schema definition
	isOrgAdmin: false,
	hasOrgSettingsAccess: true,
	hasXSurflogAccess: false,
	temp_orgs: null,
	organisation_uuid: null,
	isAuthenticated: false,
	tokenExpiry: null,
	jwtPayload: null,
	isSuperAdmin: false,
	hasVertexRole: false,
	roles: [],
	vertex_roles: [],
	token: null,
	loginLoop: 0,
	isTokenExpired: false,
	isVertexAnalyst: false,
	isForbidden: false,
	fromSwitchBool: false,
	fromAuthSwitchProduct: false,
	fromAuthSwitchProductId: 0,
	logo: ''
});

const getters = {}

const actions = {
	resetState({ commit }) {
		commit("resetState")
	},

	async handleAuthentication({ commit, state }, { accessToken, decodedToken, orgSelected }) {
		// 1. Clear out stale organisation state entries from older sessions
		commit("resetState");

		if (typeof orgSelected !== 'undefined' && orgSelected !== null) {
			commit("setFromSwitchProduct", orgSelected);
		}

		commit("setAccessToken", accessToken);
		commit("setJwtPayload", decodedToken);

		if (state.isSuperAdmin) {
			try {
				const data = await getXSurflogOrganisations(accessToken);
				commit("setSuperAdminOrganisations", data.organisations);
			} catch (error) {
				console.error("Failed to fetch super admin organisations:", error);
				return false;
			}
		}

		commit("authenticated", true);
		return true;
	},

	logout({ commit, state }) {
		let logoutUrl = `${app.config.globalProperties.$config.getLoginLink()}&logout=true`
		if (state.loginLoop > 0) logoutUrl = `${logoutUrl}&login_loop=${state.loginLoop}`;
		if (!state.isSuperAdmin || !state.hasXSurflogAccess) {
			logoutUrl = `${app.config.globalProperties.$config.getUserProfileLink()}`;
		}
		if (state.isTokenExpired) {
			logoutUrl = `${app.config.globalProperties.$config.getLoginLink()}&logout=true&error=TOKEN_EXPIRE`
		}
		if (!state.isAuthenticated) {
			logoutUrl = `${app.config.globalProperties.$config.getLoginLink()}&logout=true`;
		}
		if (state.redirectLink != null) {
			const separator = logoutUrl.includes('?') ? '&' : '?';
			logoutUrl = `${logoutUrl}${separator}redirectTo=${state.redirectLink}`;
		}
		if (state.isForbidden) {
			logoutUrl = `${app.config.globalProperties.$config.getUserProfileLink()}`;
		}

		commit("resetState");
		window.location.href = logoutUrl
	},
};

const mutations = {
	resetState(state) {
		Object.assign(state, defaultState());
	},

	setAccessToken(state, accessToken) {
		state.accessToken = accessToken;
	},

	// Replace these mutations inside your auth.js file

	setJwtPayload(state, payload) {
		state.jwtPayload = payload;
		state.tokenExpiry = payload.expires_at;

		let xsurflogProductExist = false;
		let org_name = "";
		let org_id = 0;
		//let org_admin = false;
		let matched_org_uuid = payload.organisation_uuid || ""; // Fallback to root token uuid

		if (payload && payload.organisation_role) {
			for (const org_role in payload.organisation_role) {
				const roleObj = payload.organisation_role[org_role];

				// If coming from a product/org switch, skip items that don't match the target ID
				if (state.fromAuthSwitchProduct && state.fromAuthSwitchProductId != 0) {
					if (Number(roleObj.organisation_id) !== Number(state.fromAuthSwitchProductId)) {
						continue;
					}
				} else if (payload.organisation_uuid) {
					// Context recovery / direct switch from other platforms using the root JWT token uuid
					const roleUuid = roleObj.orguuid || roleObj.organisation_uuid || roleObj.org_uuid || "";
					if (roleUuid && roleUuid !== payload.organisation_uuid) {
						continue;
					}
				}

				org_name = roleObj.organisation;
				org_id = roleObj.organisation_id;
				state.isOrgAdmin = roleObj.is_organisation_admin;

				// Look for a nested UUID inside the specific organisation role object if it exists
				if (roleObj.orguuid) matched_org_uuid = roleObj.orguuid;
				else if (roleObj.organisation_uuid) matched_org_uuid = roleObj.organisation_uuid;
				else if (roleObj.org_uuid) matched_org_uuid = roleObj.org_uuid;

				if (roleObj.products) {
					for (const product in roleObj.products) {
						if (roleObj.products[product].product == "xsurflog_browser_phishing_protection" || roleObj.products[product].product == "xsurflog") {
							xsurflogProductExist = true;
						}
						if (state.isOrgAdmin == false && xsurflogProductExist) {
							if (roleObj.products[product].roles) {
								state.hasXSurflogAccess = roleObj.products[product].roles.includes("admin") || roleObj.products[product].roles.includes("xsurflog_admin") || roleObj.products[product].roles.includes("xsurflog_analyst"); //
							}
						} else if (state.isOrgAdmin == true && xsurflogProductExist) {
							state.hasXSurflogAccess = true;
							state.hasOrgSettingsAccess = true;
						}
					}
				}
				if (xsurflogProductExist) break;
			}
		}

		// Standardize baseline state properties
		state.organisation_id = matched_org_uuid;
		state.organisation_uuid = matched_org_uuid;
		state.organisation_name = org_name;
		state.org_id = org_id;
		state.logo = org_name ? org_name.charAt(0) : '';

		if (xsurflogProductExist && matched_org_uuid) {
			CheckAccount(matched_org_uuid, org_name);
		}

		if (payload && payload.roles) {
			state.isSuperAdmin = payload.roles.includes("super_admin") || payload.roles.includes("xsurflog_analyst");
			state.isVertexAnalyst = payload.roles.includes("xsurflog_analyst");
			let isSup = payload.roles.includes("super_admin") || payload.roles.includes("xsurflog_analyst");

			if (state.isVertexAnalyst && xsurflogProductExist) {
				state.hasXSurflogAccess = true;
			}
			if (isSup) {
				state.hasOrgSettingsAccess = true;
			} else if (state.isOrgAdmin && xsurflogProductExist) {
				state.hasOrgSettingsAccess = true;
			} else {
				state.hasOrgSettingsAccess = false;
			}
		}

		if (state.isSuperAdmin) {
			state.hasXSurflogAccess = true;
		}

		// FIX: Instead of saving the raw token payload for standard users, 
		// construct a normalized object matching the database schema properties (.orguuid)
		if (state.hasXSurflogAccess && !state.isSuperAdmin) {
			state.organisation = {
				id: state.org_id,
				name: state.organisation_name,
				orguuid: state.organisation_uuid
			};
			return;
		}

		if ((!xsurflogProductExist || !state.hasXSurflogAccess) && !state.isSuperAdmin) {
			let logoutUrl = `${app.config.globalProperties.$config.getUserProfileLink()}`;
			Object.assign(state, defaultState());
			window.location.href = logoutUrl;
			return;
		}
	},

	setSuperAdminOrganisations(state, organisations) {
		if (organisations && organisations.length > 0) {
			state.organisations = organisations;
			state.temp_orgs = organisations;

			if (state.fromAuthSwitchProduct) {
				const targetId = Number(state.fromAuthSwitchProductId);
				for (let x = 0; x < state.organisations.length; x++) {
					if (Number(state.organisations[x].id) === targetId) {
						state.organisation = state.organisations[x];
						state.organisation_name = state.organisations[x].name;
						state.organisation_uuid = state.organisations[x].orguuid;

						state.organisation_id = state.organisations[x].orguuid;
						state.org_id = state.organisations[x].id;

						state.logo = state.organisations[x].name.charAt(0);
						break;
					}
				}
				state.fromAuthSwitchProduct = false;
				state.fromAuthSwitchProductId = 0;
			} else if (state.jwtPayload && state.jwtPayload.organisation_uuid) {
				const targetUuid = state.jwtPayload.organisation_uuid;
				for (let x = 0; x < state.organisations.length; x++) {
					if (state.organisations[x].orguuid === targetUuid) {
						state.organisation = state.organisations[x];
						state.organisation_name = state.organisations[x].name;
						state.organisation_uuid = state.organisations[x].orguuid;

						state.organisation_id = state.organisations[x].orguuid;
						state.org_id = state.organisations[x].id;

						state.logo = state.organisations[x].name.charAt(0);
						break;
					}
				}
			}

			// Fallback safely to the first organisation if no switch target matched
			if (state.organisation == null || state.organisation == undefined) {
				state.organisation = organisations[0];
				state.organisation_name = organisations[0].name;
				state.organisation_uuid = organisations[0].orguuid;

				state.organisation_id = organisations[0].orguuid;
				state.org_id = organisations[0].id;

				state.logo = organisations[0].name.charAt(0);
				CheckAccount(state.organisation.orguuid, state.organisation.name);
			}

			if (state.organisation_name) {
				state.logo = state.organisation_name.charAt(0);
			}

			// Dynamically update org admin and settings access flags when switching organisation
			let orgAdmin = false;
			if (state.jwtPayload && state.jwtPayload.organisation_role && state.organisation) {
				for (const org_role in state.jwtPayload.organisation_role) {
					const roleObj = state.jwtPayload.organisation_role[org_role];
					const roleUuid = roleObj.orguuid || roleObj.organisation_uuid || roleObj.org_uuid || "";
					if (
						Number(roleObj.organisation_id) === Number(state.organisation.id) ||
						(roleUuid && roleUuid === state.organisation.orguuid)
					) {
						orgAdmin = roleObj.is_organisation_admin === true;
						break;
					}
				}
			}
			state.isOrgAdmin = orgAdmin;

			// Re-evaluate hasOrgSettingsAccess
			const isSup = state.isSuperAdmin;
			if (isSup || state.isOrgAdmin) {
				state.hasOrgSettingsAccess = true;
			} else {
				state.hasOrgSettingsAccess = false;
			}
		}
	},

	// setJwtPayload(state, payload) {
	//     state.jwtPayload = payload;
	//     state.tokenExpiry = payload.expires_at;

	//     let xsurflogProductExist = false;
	//     let org_name = ""
	//     let org_id = 0
	//     let org_admin = false

	//     if (payload && payload.organisation_role) {
	//         for (const org_role in payload.organisation_role) {
	//             if (payload.organisation_role[org_role].products) {
	//                 if (state.fromAuthSwitchProduct && state.fromAuthSwitchProductId != 0) {
	//                     if (payload.organisation_role[org_role].organisation_id != state.fromAuthSwitchProductId) {
	//                         continue;
	//                     }
	//                 }
	//                 org_name = payload.organisation_role[org_role].organisation;
	//                 org_id = payload.organisation_role[org_role].organisation_id;
	//                 state.isOrgAdmin = org_admin = payload.organisation_role[org_role].is_organisation_admin;

	//                 for (const product in payload.organisation_role[org_role].products) {
	//                     if (payload.organisation_role[org_role].products[product].product == "xsurflog_browser_phishing_protection" || payload.organisation_role[org_role].products[product].product == "xsurflog") {
	//                         xsurflogProductExist = true;
	//                     }
	//                     if (state.isOrgAdmin == false && xsurflogProductExist) {
	//                         if (payload.organisation_role[org_role].products[product].roles) {
	//                             state.hasXSurflogAccess = payload.organisation_role[org_role].products[product].roles.includes("admin") || payload.organisation_role[org_role].products[product].roles.includes("xsurflog_admin") || payload.organisation_role[org_role].products[product].roles.includes("xsurflog_analyst");
	//                         }
	//                     } else if (state.isOrgAdmin == true && xsurflogProductExist) {
	//                         state.hasXSurflogAccess = true;
	//                         state.hasOrgSettingsAccess = true;
	//                     }
	//                 }
	//                 if (xsurflogProductExist) break;
	//             }
	//         }
	//     }

	//     state.organisation_id = payload.organisation_uuid;
	//     state.organisation_uuid = payload.organisation_uuid;
	//     state.organisation_name = org_name;
	//     state.org_id = org_id;
	//     state.logo = org_name ? org_name.charAt(0) : '';

	//     if (xsurflogProductExist) {
	//         CheckAccount(payload.organisation_uuid, org_name);
	//     }

	//     if (payload && payload.roles) {
	//         state.isSuperAdmin = payload.roles.includes("super_admin") || payload.roles.includes("xsurflog_analyst");
	//         state.isVertexAnalyst = payload.roles.includes("xsurflog_analyst");
	//         let isSup = payload.roles.includes("super_admin");

	//         if (state.isVertexAnalyst && xsurflogProductExist) {
	//             state.hasXSurflogAccess = true;
	//         }
	//         if (isSup) {
	//             state.hasOrgSettingsAccess = true;
	//         } else if ((state.isVertexAnalyst) && state.isOrgAdmin) {
	//             state.hasOrgSettingsAccess = true;
	//         } else {
	//             state.hasOrgSettingsAccess = false;
	//         }
	//     }

	//     if (state.isSuperAdmin) {
	//         state.hasXSurflogAccess = true;
	//     }

	//     if (state.hasXSurflogAccess && !state.isSuperAdmin) {
	//         state.organisation = payload;
	//         return;
	//     }

	//     if ((!xsurflogProductExist || !state.hasXSurflogAccess) && !state.isSuperAdmin) {
	//         let logoutUrl = `${app.config.globalProperties.$config.getUserProfileLink()}`;
	//         Object.assign(state, defaultState());
	//         window.location.href = logoutUrl;
	//         return;
	//     }
	// },

	// setSuperAdminOrganisations(state, organisations) {
	//     if (organisations && organisations.length > 0) {
	//         state.organisations = organisations;
	//         state.temp_orgs = organisations;

	//         if (state.fromAuthSwitchProduct) {
	//             const targetId = Number(state.fromAuthSwitchProductId);
	//             for (let x = 0; x < state.organisations.length; x++) {
	//                 if (Number(state.organisations[x].id) === targetId) {
	//                     state.organisation = state.organisations[x];
	//                     state.organisation_name = state.organisations[x].name;
	//                     state.organisation_uuid = state.organisations[x].orguuid;

	//                     // 2. CRITICAL FIX: Synchronize missing root state items
	//                     state.organisation_id = state.organisations[x].orguuid;
	//                     state.org_id = state.organisations[x].id;

	//                     state.logo = state.organisations[x].name.charAt(0);
	//                     break;
	//                 }
	//             }
	//             state.fromAuthSwitchProduct = false;
	//             state.fromAuthSwitchProductId = 0;
	//         }

	//         // Fallback cleanly to the default first record if target selection wasn't processed
	//         if (state.organisation == null || state.organisation == undefined) {
	//             state.organisation = organisations[0];
	//             state.organisation_name = organisations[0].name;
	//             state.organisation_uuid = organisations[0].orguuid;

	//             // 3. CRITICAL FIX: Synchronize missing fallback root items
	//             state.organisation_id = organisations[0].orguuid;
	//             state.org_id = organisations[0].id;

	//             state.logo = organisations[0].name.charAt(0);
	//             CheckAccount(state.organisation.orguuid, state.organisation.name);
	//         }

	//         if (state.organisation_name) {
	//             state.logo = state.organisation_name.charAt(0);
	//         }
	//     }
	// },

	authenticated(state, authenticated) {
		state.isAuthenticated = authenticated;
	},
	setIsTokenExpired(state) {
		state.isTokenExpired = true;
	},
	setOrganisation(state, organisation) {
		state.organisation_name = organisation.name;
		state.logo = organisation.name.charAt(0);
		state.organisation_uuid = organisation.orguuid;
		state.organisation_id = organisation.orguuid;
		state.org_id = organisation.id;
		state.organisation = organisation;

		// Dynamically update org admin and settings access flags when switching organisation
		let orgAdmin = false;
		if (state.jwtPayload && state.jwtPayload.organisation_role) {
			for (const org_role in state.jwtPayload.organisation_role) {
				const roleObj = state.jwtPayload.organisation_role[org_role];
				const roleUuid = roleObj.orguuid || roleObj.organisation_uuid || roleObj.org_uuid || "";
				if (
					Number(roleObj.organisation_id) === Number(organisation.id) ||
					(roleUuid && roleUuid === organisation.orguuid)
				) {
					orgAdmin = roleObj.is_organisation_admin === true;
					break;
				}
			}
		}
		state.isOrgAdmin = orgAdmin;

		// Re-evaluate hasOrgSettingsAccess
		const isSup = state.isSuperAdmin;
		if (isSup || state.isOrgAdmin) {
			state.hasOrgSettingsAccess = true;
		} else {
			state.hasOrgSettingsAccess = false;
		}
	},
	setFromSwitchProduct(state, id) {
		state.fromAuthSwitchProduct = true;
		state.fromAuthSwitchProductId = Number(id); // Cast explicitly to integer
	},
	searchOrg(state, key) {
		if (key == "") {
			state.temp_orgs = state.organisations;
		} else {
			state.temp_orgs = state.organisations.filter((e) => {
				return e.name.toLowerCase().includes(key.toLowerCase());
			});
		}
	},
	setOrganisations(state, organisations) {
		state.organisations = organisations;
		state.temp_orgs = organisations;
	},
	setRedirectLink(state, link) {
		state.redirectLink = link;
	},
	fromSwitch(state, val) {
		state.fromSwitchBool = val;
	}
};

async function getXSurflogOrganisations(token) {
	const response = await fetch(process.env.VUE_APP_API_BASE_URL + "/api/main/v1/auth/admin-common/get-xsurflog-organisations", {
		headers: {
			"Content-Type": "application/json",
			"Authorization": "Bearer " + token
		},
	});
	return response.json();
}

async function CheckAccount(OrgUuid, Name) {
	try {
		const payLoad = { org_uuid: OrgUuid, name: Name }
		fetch(process.env.VUE_APP_API_BASE_URL + "/api/main/v1/auth/non-login/check-account-exist", {
			method: "POST",
			body: JSON.stringify(payLoad),
			headers: { "Content-type": "application/json; charset=UTF-8" }
		});
	} catch (error) {
		console.log(error)
	}
}

export default {
	name: "auth",
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};